import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

// Icons
import {
  FaRegFile,
  FaRegFilePdf,
  FaRegFileWord,
  FaRegFileExcel,
  FaRegFileVideo,
  FaRegFileAudio,
  FaRegFileImage
} from "react-icons/fa";

// Utils
import * as urls from '../../utils/urls';

// Types
import { UploadFile } from '../../types';

// Styles
import styles from './style.module.scss';

interface FilePreviewProps {
  fileId: string;
  onRemove?: (fileId: string) => void;
}

const FilePreviewBadge: React.FC<FilePreviewProps> = ({ fileId, onRemove = undefined }) => {
  // Hooks
  const { t } = useTranslation();

  // States
  const [file, setFile] = useState<UploadFile | null>(null);
  // Refs

  // Load Method
  const loadFile = useCallback(async () => {
    try {
      const options = {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      };

      const response = await fetch(urls.uploadUrl(fileId), options);
      if (response.ok) {
        const data = await response.json();
        setFile(data)
      } else {
        console.error('Failed to load file');
      }
    } catch (error) {
      console.error(error);
    }
  }, [fileId]);

  useEffect(() => {
    if (fileId) loadFile();
  }, [fileId]);

  // Handlers
  const handleRemove = useCallback(() => {
    if (onRemove) onRemove(fileId);
  }, [fileId, onRemove]);


  const renderIcon = useCallback(() => {
    if (file) {
      const contentType = file.content_type || '';
      if (contentType.includes('image'))
        return <FaRegFileImage className={styles.icon_icon} />;
      if (contentType.includes('video'))
        return <FaRegFileVideo className={styles.icon_icon} />;
      if (contentType.includes('audio'))
        return <FaRegFileAudio className={styles.icon_icon} />;
      if (contentType.includes('application/pdf'))
        return <FaRegFilePdf className={styles.icon_icon} />;
      if (contentType.includes('application/msword'))
        return <FaRegFileWord className={styles.icon_icon} />;
      if (contentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document'))
        return <FaRegFileWord className={styles.icon_icon} />;
      if (contentType.includes('application/vnd.ms-excel'))
        return <FaRegFileExcel className={styles.icon_icon} />;
      if (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'))
        return <FaRegFileExcel className={styles.icon_icon} />;
      return <FaRegFile className={styles.icon_icon} />;
    }

    return <FaRegFile />;
  }, [file]);

  const renderSize = useCallback(() => {
    if (file) {
      const size = file.byte_size || 0;
      const sizeInMB = size / 1024 / 1024;
      return (
        <Typography variant='body2' color='textSecondary' className={styles.size}>
          {sizeInMB.toFixed(2)} MB
        </Typography>
      )
    }

    return '--';
  }, [file]);


  return (
    <div className={styles.root}>
      <div className={styles.data}>
        <div className={styles.icon}>
          {renderIcon()}
        </div>

        <div className={styles.header}>
          <Typography variant='body2' color='textPrimary' className={styles.title}>
            {file?.filename}
          </Typography>

          {renderSize()}
        </div>
      </div>

      {onRemove
        ? (
          <IconButton
            onClick={handleRemove}
            className={styles.remove}
          >
            <CloseIcon />
          </IconButton>
        )
        : <div className={styles.remove} />
      }
    </div>
  );
};

export default FilePreviewBadge;
